import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';

import {
  AboutPanelComponent,
  AboutPanelInputs,
  LegalDocumentsService,
  PermissionKey,
  PermissionsState,
  SetEnvironment,
  SidebarNavigationItem,
  SidePanelV3,
  SidepanelTypesV3,
  UserState,
} from '@gea/digital-ui-lib';
import { LOGOUT_REQUESTED } from './app.constants';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs';
import { ActivationEnd, Router } from '@angular/router';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { ENVIRONMENT_CONFIG, EnvironmentConfiguration } from '../environments/models/environment.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { UserLocaleService } from '@shared/services/user-locale/user-locale.service';
import { registerLocaleData } from '@angular/common';
import { DownloadGeneratedReport, ResetReportGeneration, ResumeReportGeneration } from '@shared/redux/report-generation.actions';
import { MachineService } from '@shared/services/machine/machine.service';
import { GetMachineReportsHistory } from './features/machine-overview/pages/machine-report/redux/machine-reports.actions';
import { ReportGenerationStorageService } from '@shared/services/report-generation-storage/report-generation-storage.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: false,
})
export class AppComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly destroyed = inject(DestroyRef);
  private readonly legalDocumentsService = inject(LegalDocumentsService);
  private readonly loadingSpinnerService = inject(LoadingSpinnerService);
  readonly environment = inject<EnvironmentConfiguration>(ENVIRONMENT_CONFIG);

  // <CHANGE ME>
  readonly APP_TITLE = 'APP-TITLE';

  tokenReady = false;
  userReady = false;
  noAuth = false;
  userCountry = 'US';
  currentLanguage = '';
  loggedOutPageReady = false;
  headerTitle = this.APP_TITLE;

  // <CHANGE ME> - Add the navigation items for the Blueprint App
  navItems: SidebarNavigationItem[] = [
    {
      label: 'UI-LIB.GENERAL.HOME',
      route: '/machine-dashboard',
      icon: 'apps',
    },
  ];
  adminNavItems: SidebarNavigationItem[] = [];
  botItems: SidePanelV3[] = [];
  // <CHANGE ME> - Add the routes that do not require authentication
  readonly NO_AUTH_ROUTES: string[] = ['about-gea', 'report'];
  protected readonly isPDFReportPage = signal<boolean>(false);

  private permissions: PermissionKey[] = [];
  public isLoading$ = this.loadingSpinnerService.loading$;

  private readonly userLocaleService = inject(UserLocaleService);
  private readonly geaPUID = inject(MachineService).machineUUID;
  private readonly reportGenerationStorage = inject(ReportGenerationStorageService);
  private readonly actions$ = inject(Actions);

  private readonly markTokenReady = this.store.select(UserState.token).subscribe(() => {
    this.tokenReady = true;
  });

  private readonly syncLoggedOutPageReady = this.store
    .select(UserState.loggedOutPageReady)
    .pipe(takeUntilDestroyed(this.destroyed))
    .subscribe((ready) => {
      this.loggedOutPageReady = !!ready;
    });

  private readonly resetReadinessOnLogoutRequest = this.store
    .select(UserState.authState)
    .pipe(takeUntilDestroyed(this.destroyed))
    .subscribe((authState) => {
      if (authState === LOGOUT_REQUESTED) {
        this.tokenReady = false;
        this.loggedOutPageReady = false;
      }
    });

  private readonly syncLayoutWithActivatedRoute = this.router.events
    .pipe(
      takeUntilDestroyed(this.destroyed),
      filter((event) => event instanceof ActivationEnd)
    )
    .subscribe((event) => {
      this.noAuth = this.NO_AUTH_ROUTES.includes(event.snapshot?.routeConfig?.path || '');
      this.isPDFReportPage.set(event.snapshot?.routeConfig?.path === 'report');

      if (event instanceof ActivationEnd) {
        this.headerTitle = this.getHeaderTitle();
      }
    });

  ngOnInit(): void {
    this.checkUserPermission();
    this.handleReportGeneration();
    this.store
      .select(UserState.user)
      .pipe(
        takeUntilDestroyed(this.destroyed),
        filter((user) => !!user.email)
      )
      .subscribe(async (user) => {
        if (user?.country) {
          this.userCountry = user.country;
        }

        if (user?.timeZone) {
          this.userLocaleService.setUserTimezone(user.timeZone.zoneId);
          this.userLocaleService.setUserZoneOffset(user.timeZone.offset);
        }

        const selectedLanguage = localStorage.getItem('language');
        if (user?.language) {
          this.translate.use(user?.language);
          await this.handleUserLocale(user.language);
        } else if (selectedLanguage) {
          this.translate.use(selectedLanguage);
        } else {
          this.translate.use(this.translate.defaultLang ?? 'en-US');
        }
        this.userReady = true;
      });

    if (this.environment.name) {
      this.store.dispatch(new SetEnvironment(this.environment.name));
    }
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyed)).subscribe((event: LangChangeEvent) => {
      this.currentLanguage = event.lang;
      this.botItems = [
        {
          key: 'contact',
          icon: 'chat',
          label: 'UI-LIB.NAV-BAR.CONTACT',
          type: SidepanelTypesV3.CONTACT,
          supportAppBaseUrl: this.environment.support.redirectURL,
        },
        {
          key: 'about',
          icon: 'c-info',
          label: 'UI-LIB.NAV-BAR.ABOUT',
          type: SidepanelTypesV3.FOOTER,
          ref: AboutPanelComponent,
          inputs: {
            copyright: 'GEA Group Services GmbH 2023',
            footers: [
              {
                key: 'imprint-header',
                header: 'UI-LIB.FOOTER.IMPRINT',
                content: [
                  {
                    key: 'imprint',
                    icon: 'pdf',
                    label: 'UI-LIB.FOOTER.IMPRINT',
                    action: () => this.legalDocumentsService.openImprint(),
                  },
                ],
              },
              {
                key: 'privacy-header',
                header: 'UI-LIB.FOOTER.DATAPRIVACY',
                content: [
                  {
                    key: 'privacy',
                    icon: 'pdf',
                    label: 'UI-LIB.FOOTER.DATAPRIVACY',
                    action: () => this.legalDocumentsService.openDataPrivacy(),
                  },
                ],
              },
              {
                key: 'terms-header',
                header: 'UI-LIB.FOOTER.TERMS-AND-CONDITIONS',
                content: [
                  {
                    key: 'terms',
                    icon: 'pdf',
                    label: 'UI-LIB.FOOTER.TERMS-AND-CONDITIONS',
                    action: () => this.legalDocumentsService.openTermsAndConditions(),
                  },
                ],
              },
              {
                key: 'cookie-settings',
                header: 'UI-LIB.FOOTER.COOKIE-SETTINGS',
                content: [
                  {
                    key: 'cookies',
                    label: 'UI-LIB.FOOTER.COOKIES',
                    action: () => this.legalDocumentsService.openCookieSettings(),
                  },
                ],
              },
            ],
          } as AboutPanelInputs,
        },
      ] as SidePanelV3[];
    });
    this.translate.setDefaultLang('de-DE');
    this.addCookielawScriptTag();
  }

  private async handleUserLocale(language: string) {
    const key = language.split('-')[0];
    await this.loadLocale(key).then(() => {
      this.userLocaleService.setUserLocale(language);
    });
  }

  addCookielawScriptTag() {
    const cookielawId = this.environment.cookielawId;
    if (cookielawId) {
      const script = document.createElement('script');
      script.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
      script.type = 'text/javascript';
      script.charset = 'UTF-8';
      script.setAttribute('data-domain-script', cookielawId);
      const head = document.head;
      const firstScriptTag = head.querySelector('script');
      if (firstScriptTag) {
        head.insertBefore(script, firstScriptTag);
      } else {
        head.appendChild(script);
      }
    }
  }

  checkUserPermission() {
    this.store.select(PermissionsState.userPermissions).subscribe((permissions: PermissionKey[]) => {
      this.permissions = permissions;
    });
  }

  getHeaderTitle() {
    return this.APP_TITLE;
  }

  private async loadLocale(localeId: string) {
    const importer = UserLocaleService.LOCALE_IMPORT_MAP[localeId];

    if (!importer) {
      console.warn(`No locale data found for: ${localeId}`);
      return;
    }

    try {
      const localeModule = await importer();
      registerLocaleData(localeModule.default);
    } catch (err) {
      console.error(`Failed to load locale data for ${localeId}`, err);
    }
  }

  private handleReportGeneration() {
    this.store
      .select(UserState.token)
      .pipe(
        filter((token) => !!token),
        take(1),
        takeUntilDestroyed(this.destroyed)
      )
      .subscribe(() => {
        const persistedRequest = this.reportGenerationStorage.read();

        if (persistedRequest) {
          this.store.dispatch(new ResumeReportGeneration(persistedRequest));
        }
      });

    this.actions$.pipe(ofActionSuccessful(DownloadGeneratedReport), takeUntilDestroyed(this.destroyed)).subscribe(() => {
      this.store.dispatch(new ResetReportGeneration());

      if (this.geaPUID()) {
        this.store.dispatch(new GetMachineReportsHistory(this.geaPUID(), 0));
      }
    });
  }
}
